import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, ChevronRight, ChevronLeft, PartyPopper } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { getCart, getCartMode, getIdempotencyKey, getPromoCode, clearCart } from "../../lib/cart";

const STEPS = ["Vos informations", "Événement & livraison", "Récapitulatif", "Confirmation"];

export default function DemandePage() {
  const [step, setStep] = useState(0);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [confirmation, setConfirmation] = useState(null);
  const [terms, setTerms] = useState(null);
  const [promoPreview, setPromoPreview] = useState(null);
  const [estimate, setEstimate] = useState(null);

  const field = (id) => document.getElementById(id)?.value?.trim() || "";
  const cartItems = () => getCart();
  const materialTotal = () => cartItems().reduce((s, i) => s + i.price * i.qty, 0);
  const optionsTotal = () => cartItems().reduce((s, i) => s + (i.option_choices || []).reduce((x, ch) => x + (ch.per_unit ? ch.price_ttc * i.qty : ch.price_ttc), 0), 0);
  const discount = promoPreview?.discount_amount || 0;
  useEffect(() => {
    if (step === 2 && getCartMode() === "livraison") {
      const address = field("d-adresse");
      api.post("/delivery/estimate", { mode: "livraison", address, material_total: materialTotal() })
        .then((r) => setEstimate(r.data))
        .catch(() => setEstimate(null));
    } else if (step === 2) {
      setEstimate(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const discountedTotal = () => Math.max(0, materialTotal() + optionsTotal() - discount + (getCartMode() === "livraison" ? estimate?.price || 0 : 0));

  useEffect(() => {
    api.get("/terms/current").then((r) => setTerms(r.data)).catch(() => setTerms(null));
  }, []);

  useEffect(() => {
    const code = getPromoCode();
    if (step === 2 && code && cartItems().length > 0) {
      api.post("/promotions/validate", { code, items: cartItems().map((i) => ({ product_slug: i.slug, variant_id: i.variant_id || null, qty: i.qty, option_ids: i.option_ids || [], option_choice_ids: i.option_choice_ids || [] })) })
        .then((r) => setPromoPreview(r.data))
        .catch(() => setPromoPreview(null));
    }
  }, [step]);

  const submitRequest = async () => {
    setSending(true);
    setError(null);
    try {
      const { data } = await api.post("/requests", {
        customer: { first_name: field("d-prenom"), last_name: field("d-nom"), email: field("d-email"), phone: field("d-tel") || null },
        event: {
          type: (field("d-type") || "autre").toLowerCase(),
          start_date: field("d-date-debut"),
          end_date: field("d-date-fin"),
          venue_address: field("d-adresse") || null,
          guests: Number(field("d-invites")) || null,
        },
        items: getCart().map((i) => ({ product_slug: i.slug, variant_id: i.variant_id || null, qty: i.qty, option_ids: i.option_ids || [], option_choice_ids: i.option_choice_ids || [] })),
        delivery: { mode: getCartMode(), address: field("d-adresse") || null, distance_km: null },
        promo_code: getPromoCode() || null,
        idempotency_key: getIdempotencyKey(),
      });
      setConfirmation(data);
      clearCart();
      setStep(3);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="container-ae max-w-3xl py-12 md:py-16" data-testid="demande-page">
      <h1 className="text-3xl sm:text-4xl text-deepgreen" data-testid="demande-title">Finaliser ma demande</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Une demande n'est pas une réservation confirmée : Art Event la vérifie, puis vous acceptez les CGV et réglez l'acompte de 30 % pour confirmer.
      </p>

      <ol className="mt-8 flex items-center gap-2 overflow-x-auto" data-testid="demande-stepper">
        {STEPS.map((s, i) => (
          <li key={s} className="flex items-center gap-2">
            <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold ${i < step ? "bg-sage text-white" : i === step ? "bg-deepgreen text-white" : "bg-muted text-muted-foreground"}`}>
              {i + 1}
            </span>
            <span className={`whitespace-nowrap text-xs font-semibold ${i === step ? "text-deepgreen" : "text-muted-foreground"}`}>{s}</span>
            {i < STEPS.length - 1 && <span className="h-px w-8 bg-border" />}
          </li>
        ))}
      </ol>

      <div className="card-ae mt-8 p-6 md:p-8">
        {step === 0 && (
          <div className="space-y-4" data-testid="demande-step-infos">
            <h2 className="font-heading text-xl text-deepgreen">Vos informations</h2>
            <div className="grid gap-4 sm:grid-cols-2">
              <div><label className="label-ae" htmlFor="d-prenom">Prénom</label><input id="d-prenom" className="input-ae" data-testid="demande-firstname" /></div>
              <div><label className="label-ae" htmlFor="d-nom">Nom</label><input id="d-nom" className="input-ae" data-testid="demande-lastname" /></div>
              <div><label className="label-ae" htmlFor="d-email">E-mail</label><input id="d-email" type="email" className="input-ae" data-testid="demande-email" /></div>
              <div><label className="label-ae" htmlFor="d-tel">Téléphone</label><input id="d-tel" className="input-ae" data-testid="demande-phone" /></div>
            </div>
            <p className="text-xs text-muted-foreground">Pas besoin de compte pour envoyer votre demande — vous le créerez après validation par Art Event.</p>
          </div>
        )}
        {step === 1 && (
          <div className="space-y-4" data-testid="demande-step-event">
            <h2 className="font-heading text-xl text-deepgreen">Événement & livraison</h2>
            <div className="grid gap-4 sm:grid-cols-2">
              <div><label className="label-ae" htmlFor="d-type">Type d'événement</label>
                <select id="d-type" className="input-ae" data-testid="demande-event-type">
                  <option>Mariage</option><option>Anniversaire</option><option>Baptême</option><option>Événement d'entreprise</option><option>Autre</option>
                </select>
              </div>
              <div><label className="label-ae" htmlFor="d-invites">Invités (estimation)</label><input id="d-invites" type="number" className="input-ae" data-testid="demande-guests" /></div>
              <div><label className="label-ae" htmlFor="d-date-debut">Début de location</label><input id="d-date-debut" type="date" className="input-ae" data-testid="demande-date-start" /></div>
              <div><label className="label-ae" htmlFor="d-date-fin">Fin de location</label><input id="d-date-fin" type="date" className="input-ae" data-testid="demande-date-end" /></div>
              <div className="sm:col-span-2"><label className="label-ae" htmlFor="d-adresse">Lieu de réception</label><input id="d-adresse" className="input-ae" data-testid="demande-venue" placeholder="Adresse complète du lieu" /></div>
              <div><label className="label-ae" htmlFor="d-creneau">Créneau souhaité</label>
                <select id="d-creneau" className="input-ae" data-testid="demande-slot"><option>Matin</option><option>Après-midi</option></select>
              </div>
            </div>
          </div>
        )}
        {step === 2 && (
          <div data-testid="demande-step-recap">
            <h2 className="font-heading text-xl text-deepgreen">Récapitulatif de la demande</h2>
            <div className="mt-4 rounded-lg bg-cream p-5 text-sm space-y-2" data-testid="demande-recap">
              {getCart().map((i) => (
                <div key={i.slug}>
                  <p className="flex justify-between"><span className="text-muted-foreground">{i.name} × {i.qty}</span><span className="font-semibold">{(i.price * i.qty + (i.option_choices || []).reduce((s, ch) => s + (ch.per_unit ? ch.price_ttc * i.qty : ch.price_ttc), 0)).toFixed(2)} €</span></p>
                  {(i.option_choices || []).map((ch) => (
                    <p key={ch.choice_name} className="flex justify-between pl-4 text-xs"><span className="font-semibold text-gold" data-testid={`demande-choice-${i.slug}`}>{ch.group_name} : {ch.choice_name}</span><span className="text-muted-foreground">+{(ch.per_unit ? ch.price_ttc * i.qty : ch.price_ttc).toFixed(2)} €</span></p>
                  ))}
                </div>
              ))}
              <p className="flex justify-between" data-testid="demande-delivery-line">
                <span className="text-muted-foreground">Livraison{estimate?.address_normalized ? ` — ${estimate.address_normalized}` : ""}{estimate?.distance_km != null ? ` (${estimate.distance_km} km depuis Artres)` : ""}</span>
                <span className="font-semibold" data-testid="demande-delivery-price">{getCartMode() === "retrait" ? "Gratuit (retrait dépôt)" : estimate ? (estimate.price > 0 ? `${estimate.price.toFixed(2)} €` : estimate.label) : "Calcul en cours…"}</span>
              </p>
              <p className="flex justify-between"><span className="text-muted-foreground">Matériel TTC</span><span className="font-semibold">{materialTotal().toFixed(2)} €</span></p>
              {optionsTotal() > 0 && <p className="flex justify-between" data-testid="demande-options"><span className="text-muted-foreground">Options</span><span className="font-semibold">{optionsTotal().toFixed(2)} €</span></p>}
              {discount > 0 && <p className="flex justify-between" data-testid="demande-discount"><span className="text-muted-foreground">Code {promoPreview.code} — remise matériel</span><span className="font-semibold text-sage">-{discount.toFixed(2)} €</span></p>}
              <p className="flex justify-between border-t border-border pt-2"><span className="font-bold text-deepgreen">Total TTC estimé</span><span className="font-bold text-deepgreen" data-testid="demande-total">{discountedTotal().toFixed(2)} €</span></p>
              <p className="flex justify-between"><span className="font-bold text-deepgreen">Acompte 30 % (après validation)</span><span className="font-bold text-deepgreen" data-testid="demande-deposit">{(discountedTotal() * 0.3).toFixed(2)} €</span></p>
            </div>
            <div className="mt-4 rounded-lg border-2 border-dashed border-sage p-4 text-sm" data-testid="demande-caution">
              <p className="font-bold text-deepgreen">Caution séparée : {getCart().reduce((s, i) => s + (i.caution_amount || 0) * i.qty, 0).toFixed(2)} €</p>
              <p className="text-muted-foreground">Distincte du total commercial — mécanisme à définir techniquement.</p>
            </div>
            <label className="mt-5 flex items-start gap-3 text-sm">
              <input type="checkbox" className="mt-0.5 h-4 w-4 accent-[#2C4C3B]" data-testid="demande-cgv-checkbox" />
              <span>J'ai pris connaissance des conditions générales de vente {terms ? <strong data-testid="demande-terms-version">version {terms.version}</strong> : "en cours de rédaction par Art Event"}. L'acceptation formelle sera demandée après validation.</span>
            </label>
          </div>
        )}
        {step === 3 && (
          <div className="py-8 text-center" data-testid="demande-step-confirmation">
            <PartyPopper className="mx-auto h-12 w-12 text-gold" aria-hidden />
            <h2 className="mt-4 font-heading text-2xl text-deepgreen" data-testid="demande-confirmation-title">Votre demande est envoyée</h2>
            {confirmation && <p className="mt-2 text-sm font-semibold text-deepgreen" data-testid="demande-reference">Référence : {confirmation.ref}</p>}
            <p className="mx-auto mt-3 max-w-md text-sm text-muted-foreground leading-relaxed">
              Art Event va vérifier la disponibilité et la cohérence de votre demande sous 24 h. Vous recevrez ensuite un e-mail pour accepter les CGV et régler l'acompte de 30 % — c'est à ce moment que votre réservation sera confirmée.
            </p>
            <Link to="/" className="btn-primary-ae mt-6" data-testid="demande-back-home">Retour à l'accueil</Link>
          </div>
        )}

        {error && (
          <p className="mt-5 rounded-lg bg-red-50 px-4 py-3 text-sm font-semibold text-red-700" data-testid="demande-error">{error}</p>
        )}
        {step < 3 && (
          <div className="mt-8 flex justify-between">
            <button
              type="button"
              onClick={() => setStep(Math.max(0, step - 1))}
              disabled={step === 0}
              className="btn-secondary-ae !px-5 !py-2.5 !text-xs disabled:opacity-40"
              data-testid="demande-prev"
            >
              <ChevronLeft className="h-4 w-4" /> Précédent
            </button>
            <button type="button" onClick={() => (step === 2 ? submitRequest() : setStep(step + 1))} disabled={sending} className="btn-primary-ae !px-5 !py-2.5 !text-xs disabled:opacity-50" data-testid="demande-next">
              {step === 2 ? (sending ? "Envoi…" : "Envoyer ma demande") : "Continuer"} <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>

      <div className="mt-6 flex items-start gap-3 rounded-lg bg-sage-light p-4 text-sm text-muted-foreground" data-testid="demande-note">
        <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-sage" />
        Aucun paiement n'est demandé maintenant. Location et coordination sont indépendantes : cette demande ne concerne que le matériel sélectionné.
      </div>
    </div>
  );
}
